import { Moon, Plus, Sunrise, Target, Trash2 } from 'lucide-react';
import { useData, useApp } from '../context.jsx';
import { getTodayKey } from '../lib/helpers.js';
import { normalizeReflection } from '../lib/normalizers.js';
import { MORNING_REFLECTIONS, EVENING_REFLECTIONS } from '../lib/constants.js';
import { IconButton } from '../components/ui.jsx';

function ReflectionPanel({ icon: Icon, title, eyebrow, prompts, values, onChange }) {
  return (
    <section className="panel reflection-panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h3>{title}</h3>
        </div>
        <Icon size={24} />
      </div>
      <div className="reflection-stack">
        {prompts.map((item) => (
          <label className="reflection-field" key={item.key}>
            <span>{item.label}</span>
            <textarea
              value={values[item.key] || ''}
              onChange={(event) => onChange(item.key, event.target.value)}
              placeholder={item.prompt}
              rows={2}
              maxLength={220}
            />
          </label>
        ))}
      </div>
    </section>
  );
}

export default function Planner() {
  const { data, updateData } = useData();
  const { drafts, setDrafts } = useApp();
  const today = getTodayKey();
  const reflection = data.planner.reflections[today] || normalizeReflection();

  const updateReflection = (period, key, value) => {
    updateData((current) => ({
      ...current,
      planner: {
        ...current.planner,
        reflections: {
          ...current.planner.reflections,
          [today]: {
            ...normalizeReflection(current.planner.reflections?.[today]),
            [period]: {
              ...normalizeReflection(current.planner.reflections?.[today])[period],
              [key]: value
            }
          }
        }
      }
    }));
  };

  const addGoal = () => {
    const value = drafts.goal.trim();
    if (!value) return;
    updateData((current) => ({
      ...current,
      planner: {
        ...current.planner,
        goals: [...current.planner.goals, { id: crypto.randomUUID(), future: value, ability: '', evidence: '', eliminate: '' }]
      }
    }));
    setDrafts((current) => ({ ...current, goal: '' }));
  };

  const updateGoal = (id, key, value) => {
    updateData((current) => ({
      ...current,
      planner: {
        ...current.planner,
        goals: current.planner.goals.map((goal) => goal.id === id ? { ...goal, [key]: value } : goal)
      }
    }));
  };

  const removeGoal = (id) => {
    updateData((current) => ({
      ...current,
      planner: {
        ...current.planner,
        goals: current.planner.goals.filter((goal) => goal.id !== id)
      }
    }));
  };

  return (
    <section className="planner-view">
      <section className="planner-hero panel wide">
        <div>
          <p className="eyebrow">Strategic calibration</p>
          <h3>10x identity, Gain evidence, protected attention</h3>
        </div>
        <Target size={42} />
      </section>

      <section className="reflection-grid wide">
        <ReflectionPanel
          icon={Sunrise}
          title="Morning"
          eyebrow="Identity entering the day"
          prompts={MORNING_REFLECTIONS}
          values={reflection.morning}
          onChange={(key, value) => updateReflection('morning', key, value)}
        />
        <ReflectionPanel
          icon={Moon}
          title="Evening"
          eyebrow="Gain and recalibration"
          prompts={EVENING_REFLECTIONS}
          values={reflection.evening}
          onChange={(key, value) => updateReflection('evening', key, value)}
        />
      </section>

      <section className="panel wide">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Future pull</p>
            <h3>10x goals</h3>
          </div>
        </div>
        <div className="mini-composer">
          <input
            value={drafts.goal}
            onChange={(event) => setDrafts((current) => ({ ...current, goal: event.target.value }))}
            onKeyDown={(event) => event.key === 'Enter' && addGoal()}
            placeholder="Add a 10x future..."
          />
          <button className="icon-button add" onClick={addGoal} type="button" aria-label="Add 10x goal">
            <Plus size={16} />
          </button>
        </div>
        <div className="goal-grid">
          {data.planner.goals.map((goal) => (
            <article className="goal-card" key={goal.id}>
              <div className="goal-card-heading">
                <input value={goal.future} onChange={(event) => updateGoal(goal.id, 'future', event.target.value)} aria-label="10x future" />
                <IconButton label="Remove goal" icon={Trash2} onClick={() => removeGoal(goal.id)} />
              </div>
              <label>Unique Ability <textarea value={goal.ability} onChange={(event) => updateGoal(goal.id, 'ability', event.target.value)} rows={2} maxLength={180} /></label>
              <label>Evidence <textarea value={goal.evidence} onChange={(event) => updateGoal(goal.id, 'evidence', event.target.value)} rows={2} maxLength={180} /></label>
              <label>Eliminate <textarea value={goal.eliminate} onChange={(event) => updateGoal(goal.id, 'eliminate', event.target.value)} rows={2} maxLength={180} /></label>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
}
